import { useAiChatContext } from 'juneau';
import { Button, Icon, Modal } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { QUICK_ACTION_PROMPTS } from './chat-content';
import { useQuickActionFilter } from './use-quick-action-filter';

interface QuickActionsModalProps {
  open: boolean;
  onClose: () => void;
}

/** Picker of common prompts — complete ones send, open-ended ones prefill the composer. */
export function QuickActionsModal({ open, onClose }: QuickActionsModalProps) {
  const t = useFeatureTranslation('aiChat');
  const { sendMessageWithText, setInput } = useAiChatContext();
  const isOffered = useQuickActionFilter();

  // A gated prompt asks about work that is not the user's to act on — hide it.
  const prompts = QUICK_ACTION_PROMPTS.filter(isOffered);

  const pick = (prompt: string, needsInput: boolean): void => {
    onClose();
    if (needsInput) {
      // Trailing space so the user types straight after the prompt.
      setInput(`${prompt} `);
      return;
    }
    void sendMessageWithText(prompt);
  };

  return (
    <Modal open={open} onClose={onClose} size='sm' testId='chat-quick-actions'>
      <Modal.Header
        onClose={onClose}
        label={t('quickActions.title')}
        description={t('quickActions.subtitle')}
      />
      <Modal.Content size='full' className='px-lg pb-lg'>
        <div className='flex flex-col gap-xs'>
          {prompts.map(({ key, needsInput = false }) => {
            const prompt = t(`quickActions.${key}`);
            return (
              <Button
                key={key}
                variant='ghost'
                onClick={() => pick(prompt, needsInput)}
                // The toolkit Button centres a nowrap span; this row is a full-width
                // icon + label + hint layout, so the span is unwrapped back into the flow.
                className='h-auto w-full justify-start gap-sm rounded-lg border border-border-base px-md py-sm text-left text-sm font-regular text-foreground-base transition-colors hover:bg-background-muted [&>span]:flex [&>span]:w-full [&>span]:items-center [&>span]:gap-sm [&>span]:whitespace-normal'
              >
                <Icon
                  name={needsInput ? 'pen' : 'bolt'}
                  className='h-4 w-4 shrink-0 text-foreground-muted'
                />
                <span className='min-w-0 flex-1'>{prompt}</span>
                {needsInput && (
                  <span className='shrink-0 text-xs text-foreground-muted'>
                    {t('quickActions.needsInput')}
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      </Modal.Content>
    </Modal>
  );
}
